import { CORRELATION_HEADERS, sanitizeEventId, sanitizeSessionId } from "@clientportal/core/observability";
import { fetchAuthSession } from "aws-amplify/auth";

import {
  type RequestCorrelation,
  createRequestCorrelation,
  recordRequestCorrelation,
} from "../../monitoring/correlation";

export interface PrepareApiHeadersDependencies {
  getAuthorizationToken: () => Promise<string | undefined>;
  createRequestCorrelation: () => Partial<RequestCorrelation>;
  recordRequestCorrelation: (correlation: Partial<RequestCorrelation>) => void;
}

const defaultDependencies: PrepareApiHeadersDependencies = {
  getAuthorizationToken: async () => {
    const { tokens } = await fetchAuthSession();
    return tokens?.idToken?.toString();
  },
  createRequestCorrelation,
  recordRequestCorrelation,
};

export const prepareApiHeaders = async (
  headers: Headers,
  dependencies: PrepareApiHeadersDependencies = defaultDependencies,
): Promise<Headers> => {
  try {
    const token = await dependencies.getAuthorizationToken();
    if (token) headers.set("Authorization", token);
  } catch {
    // A signed-out user can still call public API routes.
  }

  const candidate = dependencies.createRequestCorrelation();
  const sessionId = sanitizeSessionId(candidate.sessionId);
  const eventId = sanitizeEventId(candidate.eventId);

  if (sessionId) headers.set(CORRELATION_HEADERS.sessionId, sessionId);
  if (eventId) headers.set(CORRELATION_HEADERS.eventId, eventId);

  if (eventId) {
    dependencies.recordRequestCorrelation({
      ...(sessionId ? { sessionId } : {}),
      eventId,
    });
  }

  return headers;
};
